import React from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { NumberField } from "@/components/simulator/NumberField";
import { fmtEUR, fmtNum } from "@/lib/format";
import { Microscope, Leaf } from "lucide-react";

const SPECIES_META = {
  chlorella: { color: "#22c55e", accent: "text-green-400", label: "Chlorella vulgaris" },
  spirulina: { color: "#14b8a6", accent: "text-teal-400", label: "Spirulina" },
  scenedesmus: { color: "#84cc16", accent: "text-lime-400", label: "Scenedesmus obliquus" },
};

export const AlgaeTab = () => {
  const { inputs, summary, updateAlgaeSpecies, updateAlgaeConfig } = useSimulator();
  const speciesKeys = ["chlorella", "spirulina", "scenedesmus"];

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3">
          <Microscope className="h-7 w-7 text-emerald-400" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-100">
            Lotto 2 — Microalghe in Fotobioreattori
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Coltivazione chiusa di tre specie ad alto valore nutraceutico.
            Ripartizione modulare tra biomassa essiccata ed estratto
            supercritico SC-CO₂.
          </p>
        </div>
      </div>

      <Card className="bg-slate-900/70 border-slate-800 p-6">
        <h3 className="font-display text-lg font-semibold text-slate-100 mb-4">
          Configurazione impianto PBR
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <NumberField
            label="Volume operativo PBR"
            value={inputs.algae.pbr_volume_m3}
            onChange={(v) => {
              const scale = v / inputs.algae.pbr_volume_m3;
              updateAlgaeConfig({ pbr_volume_m3: v });
              speciesKeys.forEach((k) => {
                updateAlgaeSpecies(k, {
                  kg_year: Number((inputs.algae[k].kg_year * scale).toFixed(1)),
                });
              });
            }}
            min={12}
            max={120}
            step={1}
            unit="m³"
            testId="algae-pbr-volume"
            sliderTheme="algae-slider"
            hint="Config default 36 m³ · Optimizer confronta 36 / 45 / 50 / 60 m³"
          />
          <NumberField
            label="Produttività media"
            value={inputs.algae.productivity_g_l_d}
            onChange={(v) => updateAlgaeConfig({ productivity_g_l_d: v })}
            min={0.05}
            max={0.35}
            step={0.005}
            unit="g / L·d"
            testId="algae-productivity"
            sliderTheme="algae-slider"
            hint="0,10 prudente · 0,15 base · 0,20 ottimistico"
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {speciesKeys.map((key) => {
          const sp = inputs.algae[key];
          const r = summary.algae[key];
          const meta = SPECIES_META[key];
          return (
            <Card
              key={key}
              data-testid={`algae-species-${key}-card`}
              className="bg-slate-900/70 border-slate-800 p-6 space-y-5 relative overflow-hidden"
            >
              <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{ background: meta.color }}
              />
              <div className="flex items-center gap-2">
                <Leaf className={`h-5 w-5 ${meta.accent}`} />
                <h3 className={`font-display text-lg font-semibold ${meta.accent}`}>
                  {sp.name}
                </h3>
              </div>

              <NumberField
                label="Biomassa secca annua"
                value={sp.kg_year}
                onChange={(v) => updateAlgaeSpecies(key, { kg_year: v })}
                min={0}
                max={5000}
                step={10}
                unit="kg / anno"
                testId={`algae-species-${key}-kg`}
                sliderTheme="algae-slider"
                hint="Riferimento H2 Farm: 2.300 kg totali / 3 specie ≈ 767 kg"
              />

              <NumberField
                label="Resa polvere essiccata"
                value={sp.yield_dried}
                onChange={(v) => updateAlgaeSpecies(key, { yield_dried: v })}
                min={0}
                max={100}
                step={0.5}
                unit="%"
                testId={`algae-species-${key}-yield-dried`}
                sliderTheme="algae-slider"
                hint="Confermata: 92% biomassa residua"
              />

              <NumberField
                label="Resa estratto SC-CO₂"
                value={sp.yield_scco2}
                onChange={(v) => updateAlgaeSpecies(key, { yield_scco2: v })}
                min={0}
                max={30}
                step={0.5}
                unit="%"
                testId={`algae-species-${key}-yield-scco2`}
                sliderTheme="algae-slider"
                hint="Confermata: 6,5% · max letteratura ~15%"
              />

              <NumberField
                label="Prezzo polvere essiccata"
                value={sp.price_dried}
                onChange={(v) => updateAlgaeSpecies(key, { price_dried: v })}
                min={5}
                max={200}
                step={1}
                unit="€ / kg"
                testId={`algae-species-${key}-price-dried`}
                sliderTheme="algae-slider"
              />

              <NumberField
                label="Prezzo estratto SC-CO₂"
                value={sp.price_scco2}
                onChange={(v) => updateAlgaeSpecies(key, { price_scco2: v })}
                min={100}
                max={3000}
                step={10}
                unit="€ / kg"
                testId={`algae-species-${key}-price-scco2`}
                sliderTheme="algae-slider"
              />

              <div className="pt-4 border-t border-slate-800 space-y-2">
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Polvere essiccata ({fmtNum(r.kg_dried)} kg)</span>
                  <span className="font-mono-tab text-slate-200">{fmtEUR(r.rev_dried)}</span>
                </div>
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Estratto SC-CO₂ ({fmtNum(r.kg_scco2, 2)} kg)</span>
                  <span className="font-mono-tab text-slate-200">{fmtEUR(r.rev_scco2)}</span>
                </div>
                <div
                  data-testid={`algae-species-${key}-total`}
                  className="flex justify-between pt-2 border-t border-slate-800"
                >
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Ricavo specie
                  </span>
                  <span className={`font-display font-bold text-lg font-mono-tab ${meta.accent}`}>
                    {fmtEUR(r.total)}
                  </span>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card
        data-testid="algae-revenue-total-display"
        className="bg-gradient-to-br from-emerald-500/15 to-emerald-600/5 border border-emerald-500/30 p-5"
      >
        <div className="text-xs font-semibold tracking-wider uppercase text-emerald-300">
          Ricavo Annuo Totale Lotto 2 (Microalghe)
        </div>
        <div className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-emerald-300 font-mono-tab">
          {fmtEUR(summary.algae.total)}
        </div>
      </Card>
    </div>
  );
};
